import { useState, useRef, useCallback } from "react";

export interface UploadedFile {
  name: string;
  content: string;   // base64 data URI
  size: number;
  typeCode?: number;  // optional file type classification
}

export interface UseFormFileUploadOptions {
  maxFileSizeMb?: number;
  onError?: (message: string) => void;
}

export interface UseFormFileUploadReturn {
  isDragOver: boolean;
  fileError: string | undefined;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleFileDrop: (e: React.DragEvent) => void;
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  processFiles: (fileList: File[]) => Promise<UploadedFile[]>;
  removeFile: (files: UploadedFile[], index: number) => UploadedFile[];
}

/**
 * File upload logic hook extracted from SR/RFS (identical in both).
 *
 * Encapsulates all file upload state and handlers.
 * Used internally by FormFileUpload but can also be used standalone.
 */
export const useFormFileUpload = (
  options: UseFormFileUploadOptions = {}
): UseFormFileUploadReturn => {
  const { maxFileSizeMb = 10, onError } = options;

  const [isDragOver, setIsDragOver] = useState(false);
  const [fileError, setFileError] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const readFileAsDataUrl = useCallback(
    (file: File): Promise<UploadedFile | null> => {
      const maxBytes = maxFileSizeMb * 1024 * 1024;
      if (file.size > maxBytes) {
        const errorMsg = `File "${file.name}" exceeds ${maxFileSizeMb}MB limit`;
        setFileError(errorMsg);
        onError?.(errorMsg);
        return Promise.resolve(null);
      }
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          resolve({
            name: file.name,
            content: reader.result as string,
            size: file.size,
          });
        };
        reader.onerror = () => resolve(null);
      });
    },
    [maxFileSizeMb, onError]
  );

  const processFiles = useCallback(
    async (fileList: File[]): Promise<UploadedFile[]> => {
      setFileError(undefined);
      const results = await Promise.all(fileList.map((f) => readFileAsDataUrl(f)));
      return results.filter((f): f is UploadedFile => f !== null);
    },
    [readFileAsDataUrl]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleFileDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        processFiles(Array.from(e.dataTransfer.files));
      }
    },
    [processFiles]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        processFiles(Array.from(e.target.files));
      }
      e.target.value = "";
    },
    [processFiles]
  );

  const removeFile = useCallback(
    (files: UploadedFile[], index: number): UploadedFile[] => {
      return files.filter((_, i) => i !== index);
    },
    []
  );

  return {
    isDragOver,
    fileError,
    fileInputRef,
    handleFileDrop,
    handleFileInputChange,
    handleDragOver,
    handleDragLeave,
    processFiles,
    removeFile,
  };
};
